import {
  CreateInvoiceSubscriptionRequest,
  CreatePaymentUrlRequest,
  CreatePaymentUrlResponse,
  ListProductResponse,
  PaymentDetailsResponse,
  ProductSubscriptionResponse,
  toTier,
  UpgradeSubscriptionRequest,
} from "./Payment";
import { AxiosInstance } from "axios";
import { Company } from "@/types/CompanyInfo";
import { isEmpty } from "lodash";
import { failure, ok, pending, Result } from "@/common/common";
import { makeAutoObservable } from "mobx";
import { User } from "@/types/UserInfo";
import { SubscriptionStatusContext } from "@/common/subscription";

export class PaymentService {
  constructor(private api: AxiosInstance) {}

  upgradeSubscription(request: UpgradeSubscriptionRequest): Promise<void> {
    return this.api.post(`/payment/upgrade-subscription`, request).then((response) => {
      return response.data;
    });
  }

  createInvoiceSubscription(
    organisationNumber: string,
    request: CreateInvoiceSubscriptionRequest,
  ): Promise<void> {
    return this.api
      .post(`/payment/create-invoice-subscription/${organisationNumber}`, request)
      .then((res) => res.data);
  }

  findAvailableProducts(): Promise<ListProductResponse> {
    return this.api.get("/payment/products").then((res) => res.data);
  }

  findProductSubscription(orgNumber: string): Promise<ProductSubscriptionResponse | undefined> {
    return this.api.get(`/payment/find-subscription/${orgNumber}`).then((response) => {
      return isEmpty(response.data) ? undefined : response.data;
    });
  }

  createExternalPaymentPortalLink(organisationNumber: string): Promise<PaymentDetailsResponse> {
    return this.api.get(`/payment/payment-details/${organisationNumber}`).then((response) => {
      return response.data;
    });
  }

  getPaymentUrl(
    organisationNumber: string,
    request: CreatePaymentUrlRequest,
  ): Promise<CreatePaymentUrlResponse> {
    return this.api
      .post(`/payment/create-checkout-session/${organisationNumber}`, request)
      .then((response) => {
        return response.data;
      });
  }
}

export class SubscriptionStatusController {
  currentSubscriptionResult: Result<SubscriptionStatusContext> | undefined;
  user: User | null;

  constructor(
    private paymentService: PaymentService,
    user: User | null,
  ) {
    this.user = user;
    makeAutoObservable(this);
  }

  *initCurrentSubscription(company: Company) {
    this.currentSubscriptionResult = pending();
    try {
      const result: ProductSubscriptionResponse | undefined =
        yield this.paymentService.findProductSubscription(company.organizationnumber);
      this.currentSubscriptionResult = ok({
        subscriptionType: result?.subscriptionType,
        productDescription: result?.productDescription,
        currentPrice: result?.paidAmount,
        billInterval: result?.billInterval,
        companyName: company.name,
        orgNo: company.organizationnumber,
        email: this.user?.email ?? "",
        name: `${this.user?.firstName} ${this.user?.lastName}`,
        tier: toTier(result),
        availableEnterpriseAccounts: [],
      });
    } catch (e) {
      this.currentSubscriptionResult = failure();
    }
  }
}
